import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faSave, faSpinner, faTimes, faUser, faGlobe, faPen } from '@fortawesome/free-solid-svg-icons';

const AutomaticMessageModal = ({
    editingMessage,
    setEditingMessage,
    agents,
    allMessages = [],
    isSaving,
    onSave,
    onClose,
    isDark
}) => {
    const isEditMode = Boolean(
        (editingMessage.idMensaje && editingMessage.idMensaje > 0) ||
        (editingMessage.idCatMensajesAutomaticos && editingMessage.idCatMensajesAutomaticos > 0) ||
        (editingMessage.vClaveMensaje && editingMessage.vClaveMensaje.trim() !== '')
    );

    // Obtener registros existentes para esta categoría
    const existingCategoryRecords = React.useMemo(() => {
        if (editingMessage?.messages && Array.isArray(editingMessage.messages) && editingMessage.messages.length > 0) {
            return editingMessage.messages;
        }
        if (!editingMessage?.vClaveMensaje) return [];
        const clave = editingMessage.vClaveMensaje.trim().toUpperCase();
        return (allMessages || []).filter(m => m.vClaveMensaje && m.vClaveMensaje.trim().toUpperCase() === clave);
    }, [editingMessage?.messages, editingMessage?.vClaveMensaje, allMessages]);

    const initialTarget = editingMessage.idUsuario ? String(editingMessage.idUsuario) : 'general';

    // Lista de seleccionados: 'general' (Global) siempre incluido por default y requerido
    const [selectedTargets, setSelectedTargets] = useState(() => {
        const targets = ['general'];
        if (existingCategoryRecords.length > 0) {
            existingCategoryRecords.forEach(m => {
                if (m.idUsuario) {
                    targets.push(String(m.idUsuario));
                }
            });
        }
        if (editingMessage.idUsuario && !targets.includes(String(editingMessage.idUsuario))) {
            targets.push(String(editingMessage.idUsuario));
        }
        return Array.from(new Set(targets));
    });

    // Target actualmente activo para edición en el textarea
    const [activeTarget, setActiveTarget] = useState(initialTarget);

    // Mapa de mensajes individuales por target: { 'general': '...', '10': '...' }
    const [messagesMap, setMessagesMap] = useState(() => {
        const map = {};
        if (existingCategoryRecords.length > 0) {
            existingCategoryRecords.forEach(m => {
                const key = m.idUsuario ? String(m.idUsuario) : 'general';
                map[key] = m.vMensajePersonalizado || '';
            });
        }
        if (editingMessage.vMensajePersonalizado && !map[initialTarget]) {
            map[initialTarget] = editingMessage.vMensajePersonalizado;
        }
        return map;
    });

    // Manejar selección del Dropdown para agregar un asesor o general
    const handleSelectDropdown = e => {
        const val = e.target.value;
        if (!val) return;

        if (!selectedTargets.includes(val)) {
            setSelectedTargets(prev => [...prev, val]);
            // Inicializar con texto vacío o el texto actual si no tenía
            setMessagesMap(prev => ({
                ...prev,
                [val]: prev[val] !== undefined ? prev[val] : ''
            }));
            // Cambiar automáticamente la pestaña/edición activa al recién agregado
            setActiveTarget(val);
        }
        e.target.value = '';
    };

    // Quitar un label / tag de la lista de seleccionados (Global es siempre requerido)
    const handleRemoveTarget = (e, valToRemove) => {
        e.stopPropagation();
        if (valToRemove === 'general') return; // Global es requerido siempre, no se puede eliminar
        if (selectedTargets.length <= 1) return;

        const newTargets = selectedTargets.filter(t => t !== valToRemove);
        setSelectedTargets(newTargets);

        // Si quitamos el que estaba activo para editar, activar el primero restante (o general)
        if (activeTarget === valToRemove) {
            setActiveTarget(newTargets[0] || 'general');
        }
    };

    // Cambiar target activo al hacer clic en su label
    const handleSelectActiveTarget = val => {
        setActiveTarget(val);
    };

    // Actualizar texto del target activo
    const handleTextChange = e => {
        const val = e.target.value;
        setMessagesMap(prev => ({
            ...prev,
            [activeTarget]: val
        }));
    };

    // Obtener nombre para mostrar en el label
    const getTargetName = val => {
        if (val === 'general') return 'Global';
        const agent = (agents || []).find(a => String(a.idUsuario) === String(val));
        return agent ? (agent.vNombreCompleto || agent.vNombre || `Usuario #${val}`) : `Usuario #${val}`;
    };

    // Guardar todos los destinatarios seleccionados
    const handleSaveClick = () => {
        const targetsToSave = selectedTargets.map(t => ({
            idUsuario: t === 'general' ? null : parseInt(t, 10),
            text: (messagesMap[t] || '').trim()
        }));

        // Validar que ninguno esté vacío
        const hasEmpty = targetsToSave.some(t => !t.text);
        if (hasEmpty) return;

        onSave(targetsToSave);
    };

    const currentText = messagesMap[activeTarget] || '';
    const canSave = selectedTargets.length > 0 && selectedTargets.every(t => (messagesMap[t] || '').trim().length > 0);

    return (
        <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(3px)', zIndex: 1060 }}
        >
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div
                    className="modal-content border-0 shadow-lg"
                    style={{
                        backgroundColor: isDark ? '#121e2d' : '#ffffff',
                        borderRadius: '12px'
                    }}
                >
                    {/* Modal Header */}
                    <div
                        className="modal-header py-3 px-4 border-bottom"
                        style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    backgroundColor: isDark ? 'rgba(37, 99, 235, 0.2)' : '#dbeafe',
                                    color: isDark ? '#60a5fa' : '#2563eb'
                                }}
                            >
                                <FontAwesomeIcon icon={isEditMode ? faEdit : faPlus} style={{ fontSize: '0.9rem' }} />
                            </div>
                            <div>
                                <h6 className="modal-title fw-bold mb-0" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                                    {isEditMode ? 'Editar Categoría / Mensaje' : 'Nueva Categoría y Asignación'}
                                </h6>
                                {isEditMode && (
                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                        ID #{editingMessage.idMensaje}
                                    </small>
                                )}
                            </div>
                        </div>

                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            disabled={isSaving}
                            style={{ filter: isDark ? 'invert(1)' : 'none' }}
                        />
                    </div>

                    {/* Modal Body */}
                    <div className="modal-body p-4">
                        {/* PASO 1: Categoría / Clave */}
                        <div className="mb-3">
                            <label
                                className="form-label small fw-semibold mb-1 d-flex align-items-center gap-1"
                                style={{ color: isDark ? '#cbd5e1' : '#475569' }}
                            >
                                <span
                                    className="badge rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center"
                                    style={{ width: '18px', height: '18px', fontSize: '0.68rem' }}
                                >
                                    1
                                </span>
                                <span>Categoría / Clave Identificadora:</span>
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className="form-control form-control-sm font-monospace"
                                placeholder="Ej. BIENVENIDA, FUERA_HORARIO, DESPEDIDA..."
                                value={editingMessage.vClaveMensaje || ''}
                                onChange={e => setEditingMessage({ ...editingMessage, vClaveMensaje: e.target.value.toUpperCase() })}
                                disabled={isSaving}
                                style={{
                                    borderRadius: '8px',
                                    backgroundColor: isDark ? '#0f172a' : '#ffffff',
                                    borderColor: isDark ? '#233142' : '#cbd5e1',
                                    color: isDark ? '#f1f5f9' : '#1e293b'
                                }}
                            />
                        </div>

                        {/* PASO 2: DDL Asignar Mensaje con Labels dinámicos abajo */}
                        <div className="mb-3">
                            <label
                                className="form-label small fw-semibold mb-1 d-flex align-items-center gap-1"
                                style={{ color: isDark ? '#cbd5e1' : '#475569' }}
                            >
                                <span
                                    className="badge rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center"
                                    style={{ width: '18px', height: '18px', fontSize: '0.68rem' }}
                                >
                                    2
                                </span>
                                <span>Asignar Mensaje:</span>
                                <span className="text-danger">*</span>
                            </label>

                            {/* Dropdown / DDL */}
                            <select
                                className="form-select form-select-sm"
                                defaultValue=""
                                onChange={handleSelectDropdown}
                                disabled={isSaving}
                                style={{
                                    borderRadius: '8px',
                                    backgroundColor: isDark ? '#0f172a' : '#ffffff',
                                    borderColor: isDark ? '#233142' : '#cbd5e1',
                                    color: isDark ? '#f1f5f9' : '#1e293b'
                                }}
                            >
                                <option value="" disabled>
                                    Seleccionar para agregar Asesores...
                                </option>
                                {!selectedTargets.includes('general') && (
                                    <option value="general">🌐 Global</option>
                                )}
                                {agents && agents.length > 0 && (
                                    <optgroup label="Asesores individuales">
                                        {agents
                                            .filter(a => !selectedTargets.includes(String(a.idUsuario)))
                                            .map(a => (
                                                <option key={a.idUsuario} value={String(a.idUsuario)}>
                                                    👤 {a.vNombreCompleto || a.vNombre || `Usuario #${a.idUsuario}`}
                                                </option>
                                            ))}
                                    </optgroup>
                                )}
                            </select>

                            {/* Labels interactivos abajo del DDL */}
                            <div className="d-flex align-items-center gap-1.5 flex-wrap mt-2">
                                <small className="text-muted fw-semibold me-1" style={{ fontSize: '0.75rem' }}>
                                    Haz clic en un destinatario para redactar su mensaje:
                                </small>
                                {selectedTargets.map(val => {
                                    const isGeneral = val === 'general';
                                    const isActive = activeTarget === val;
                                    const hasContent = Boolean((messagesMap[val] || '').trim());

                                    return (
                                        <button
                                            key={val}
                                            type="button"
                                            onClick={() => handleSelectActiveTarget(val)}
                                            className="badge rounded-pill px-2.5 py-1.5 fw-semibold d-inline-flex align-items-center gap-1.5 border shadow-none"
                                            style={{
                                                fontSize: '0.8rem',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease',
                                                backgroundColor: isActive
                                                    ? (isGeneral ? '#2563eb' : '#059669')
                                                    : (isGeneral
                                                        ? (isDark ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff')
                                                        : (isDark ? 'rgba(16, 185, 129, 0.15)' : '#f0fdf4')),
                                                color: isActive
                                                    ? '#ffffff'
                                                    : (isGeneral
                                                        ? (isDark ? '#93c5fd' : '#1d4ed8')
                                                        : (isDark ? '#6ee7b7' : '#047857')),
                                                borderColor: isActive
                                                    ? (isGeneral ? '#1d4ed8' : '#047857')
                                                    : (isGeneral
                                                        ? (isDark ? '#3b82f6' : '#bfdbfe')
                                                        : (isDark ? '#10b981' : '#bbf7d0')),
                                                transform: isActive ? 'scale(1.04)' : 'none'
                                            }}
                                            title={`Clic para editar mensaje de ${getTargetName(val)}`}
                                        >
                                            <FontAwesomeIcon icon={isGeneral ? faGlobe : faUser} style={{ fontSize: '0.72rem' }} />
                                            <span>{getTargetName(val)}</span>
                                            {isActive && <FontAwesomeIcon icon={faPen} style={{ fontSize: '0.65rem' }} className="ms-0.5" />}
                                            {!hasContent && <span className="text-danger ms-0.5 fw-bold">*</span>}

                                            {!isGeneral && selectedTargets.length > 1 && (
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    className="p-0 ms-1"
                                                    onClick={e => handleRemoveTarget(e, val)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            handleRemoveTarget(e, val);
                                                        }
                                                    }}
                                                    style={{
                                                        color: isActive ? '#ffffff' : (isDark ? '#cbd5e1' : '#64748b'),
                                                        fontSize: '0.8rem',
                                                        lineHeight: 1
                                                    }}
                                                    title="Quitar asesor"
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Textarea Único: Mensaje personalizado para el target activo */}
                        <div className="mb-1">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <label
                                    className="form-label small fw-semibold mb-0"
                                    style={{ color: isDark ? '#cbd5e1' : '#475569' }}
                                >
                                    Mensaje para <span className="text-primary fw-bold">{getTargetName(activeTarget)}</span>:{' '}
                                    <span className="text-danger">*</span>
                                </label>
                                <span className="small text-muted" style={{ fontSize: '0.74rem' }}>
                                    Editando destinatario: <strong>{getTargetName(activeTarget)}</strong>
                                </span>
                            </div>

                            <textarea
                                rows="6"
                                className="form-control"
                                value={currentText}
                                onChange={handleTextChange}
                                placeholder={`Escribe el mensaje automático para ${getTargetName(activeTarget)}...`}
                                disabled={isSaving}
                                style={{
                                    borderRadius: '8px',
                                    backgroundColor: isDark ? '#0f172a' : '#ffffff',
                                    borderColor: isDark ? '#233142' : '#cbd5e1',
                                    color: isDark ? '#f1f5f9' : '#1e293b',
                                    fontSize: '0.88rem',
                                    lineHeight: '1.6'
                                }}
                            />
                            <div className="d-flex justify-content-between align-items-center small text-muted mt-1" style={{ fontSize: '0.72rem' }}>
                                <span>
                                    {selectedTargets.length > 1 && (
                                        <>
                                            Destinatarios configurados:{' '}
                                            {selectedTargets.filter(t => (messagesMap[t] || '').trim().length > 0).length} de {selectedTargets.length}
                                        </>
                                    )}
                                </span>
                                <span>{currentText.length} caracteres</span>
                            </div>
                        </div>
                    </div>

                    {/* Modal Footer */}
                    <div
                        className="modal-footer py-2.5 px-4 border-top d-flex justify-content-end gap-2"
                        style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}
                    >
                        <button
                            type="button"
                            className="btn btn-sm btn-secondary rounded-pill px-3 shadow-none"
                            onClick={onClose}
                            disabled={isSaving}
                            style={{ fontSize: '0.84rem' }}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary rounded-pill px-4 shadow-none d-flex align-items-center gap-2"
                            onClick={handleSaveClick}
                            disabled={isSaving || !canSave}
                            style={{ fontSize: '0.84rem' }}
                        >
                            {isSaving ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    <span>Guardando...</span>
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faSave} />
                                    <span>{isEditMode ? 'Guardar Cambios' : 'Guardar Mensajes'}</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AutomaticMessageModal;
